import * as amplitude from '@amplitude/analytics-browser';
import { init as initEngagementSdk } from '@amplitude/engagement-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { initialState, setting } from '@/Helpers.js';
import store from '@/store/Index.js';
import { buildAmplitudeUserTraits, getPrimaryRoleName } from '@/utilities/amplitudeUserTraits.js';
import { getAmplitudeVertical } from '@/utilities/amplitudeVertical.js';
import { syncAmplitudeUserProfile } from '@/utilities/syncAmplitudeUserProfile.js';

export {
    AMPLITUDE_GROUP_TYPE,
    AMPLITUDE_MAX_GROUPS,
    buildAmplitudeUserTraits,
    capGroupNamesForAmplitude,
    getPrimaryRoleName,
    ROLE_PRIORITY,
} from '@/utilities/amplitudeUserTraits.js';
export { syncAmplitudeUserProfile };

export const AMPLITUDE_PROXY_ORIGIN = 'https://cf-worker.edverum.com';

export const amplitudeServerUrl = () => `${AMPLITUDE_PROXY_ORIGIN}/2/httpapi`;

export const amplitudeReplayConfigServerUrl = () => `${AMPLITUDE_PROXY_ORIGIN}/config`;

export const amplitudeReplayTrackServerUrl = () => `${AMPLITUDE_PROXY_ORIGIN}/sessions/v2/track`;

/**
 * Proxy options for Amplitude Guides & Surveys (Engagement SDK).
 * @see https://amplitude.com/docs/guides-and-surveys/proxy
 * @returns {{ serverUrl: string, cdnUrl: string, mediaUrl: string }}
 */
export const amplitudeEngagementProxyOptions = () => ({
    serverUrl: AMPLITUDE_PROXY_ORIGIN,
    cdnUrl: AMPLITUDE_PROXY_ORIGIN,
    mediaUrl: AMPLITUDE_PROXY_ORIGIN,
});

/**
 * Boot options for standalone Guides & Surveys (proxy path — not engagementPlugin).
 * @param {string} userId
 * @param {string} deviceId
 * @param {Record<string, unknown>} [userProperties]
 * @returns {import('@amplitude/engagement-browser').BootOptions}
 */
export const buildAmplitudeEngagementBootOptions = (userId, deviceId, userProperties = {}) => ({
    user: {
        user_id: userId,
        device_id: deviceId,
        user_properties: userProperties,
        getSessionId: () => amplitude.getSessionId(),
    },
    integrations: [
        {
            track: event => {
                amplitude.track(event.event_type, event.event_properties ?? {});
            },
        },
    ],
});

/**
 * @param {Object} user
 * @returns {Promise<void>}
 */
const bootEngagementForUser = async user => {
    const userId = user.id.toString();
    const deviceId = amplitude.getDeviceId()?.toString() ?? userId;
    const { roleNames, groupNames } = buildAmplitudeUserTraits(user);

    await window.engagement.boot(
        buildAmplitudeEngagementBootOptions(userId, deviceId, {
            role_names: roleNames,
            group_names: groupNames,
            vertical: getAmplitudeVertical(),
        })
    );
};

/**
 * @param {number} timeoutMs
 * @param {number} intervalMs
 * @returns {Promise<void>}
 */
const waitForEngagementReady = (timeoutMs = 10000, intervalMs = 50) =>
    new Promise((resolve, reject) => {
        if (typeof window === 'undefined') {
            reject(new Error('Engagement SDK unavailable (SSR)'));
            return;
        }

        const startTime = Date.now();
        let timeoutId;

        const check = () => {
            if (typeof window.engagement?.boot === 'function' && window.engagement?.gs) {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                resolve();
                return;
            }

            if (Date.now() - startTime >= timeoutMs) {
                reject(new Error('Engagement SDK failed to load within timeout'));
                return;
            }

            timeoutId = setTimeout(check, intervalMs);
        };

        check();
    });

/**
 * Re-boot Guides & Surveys after auth user changes (e.g. impersonation).
 * @param {Object} user
 * @returns {Promise<void>}
 */
export const resyncAmplitudeEngagementUser = async user => {
    if (!user?.id || typeof window === 'undefined' || typeof window.engagement?.boot !== 'function') {
        return;
    }

    if (typeof window.engagement.shutdown === 'function') {
        window.engagement.shutdown();
    }

    await bootEngagementForUser(user);
};

/**
 * Initialize Guides & Surveys via standalone init + boot (required when using a proxy).
 * @param {string} apiKey
 * @param {Object} user
 * @returns {Promise<void>}
 */
export const bootAmplitudeEngagement = async (apiKey, user) => {
    initEngagementSdk(apiKey, amplitudeEngagementProxyOptions());
    await waitForEngagementReady();
    await bootEngagementForUser(user);
};

// Initialization promise for waiting on Amplitude to be ready
let amplitudeInitPromise = null;
let amplitudeInitResolver = null;

/**
 * Wait for Amplitude SDK to initialize with a timeout
 * @param {number} timeoutMs - Maximum time to wait in milliseconds
 * @returns {Promise<{initialized?: boolean, timedOut?: boolean}>}
 */
export const waitForAmplitudeInit = (timeoutMs = 5000) => {
    if (!amplitudeInitPromise) {
        return Promise.resolve({ initialized: false });
    }

    return Promise.race([
        amplitudeInitPromise.then(() => ({ initialized: true })),
        new Promise(resolve => setTimeout(() => resolve({ timedOut: true }), timeoutMs)),
    ]);
};

/**
 * Check if survey tracking is enabled via environment setting
 * @returns {boolean}
 */
const isSurveyTrackingEnabled = () => {
    const userSetting = initialState(`UserSettings.amplitude_survey_tracking_enabled`, null);
    const institutionSetting = initialState(`InstitutionSettings.amplitude_survey_tracking_enabled`, null);
    const envSetting = initialState(`EnvironmentSettings.amplitude_survey_tracking_enabled`, null);
    const finalValue = setting('amplitude_survey_tracking_enabled', false);

    console.debug('[Amplitude] Survey tracking setting check:', {
        userSetting,
        institutionSetting,
        envSetting,
        finalValue,
        enabled: Boolean(finalValue),
    });

    return Boolean(finalValue);
};

/**
 * Check if Amplitude is initialized and ready for tracking
 * @returns {boolean}
 */
const isAmplitudeInitialized = () => {
    try {
        const deviceId = amplitude.getDeviceId();
        return Boolean(deviceId);
    } catch (_error) {
        // Expected when Amplitude hasn't initialized yet
        return false;
    }
};

/**
 * Track when an automated survey is started
 * Used for Targeted Replay Capture (TRC) in Amplitude
 * @param {Object} assignment - The automated survey assignment
 * @returns {Promise<{tracked?: boolean, skipped?: boolean, reason?: string}>}
 */
export const trackAutomatedSurveyStarted = async assignment => {
    if (!isSurveyTrackingEnabled()) {
        console.debug('[Amplitude] Survey tracking disabled - skipping "Automated Survey Started" event');
        return { skipped: true, reason: 'disabled' };
    }

    // Wait for Amplitude to initialize (with 5 second timeout)
    const initResult = await waitForAmplitudeInit(5000);

    if (initResult.timedOut) {
        console.warn('[Amplitude] Initialization timeout - skipping "Automated Survey Started" event');
        return { skipped: true, reason: 'timeout' };
    }

    if (!initResult.initialized) {
        console.warn('[Amplitude] SDK not initialized - skipping "Automated Survey Started" event');
        return { skipped: true, reason: 'not_initialized' };
    }

    // Double-check initialization state
    if (!isAmplitudeInitialized()) {
        console.warn('[Amplitude] SDK not ready - skipping "Automated Survey Started" event');
        return { skipped: true, reason: 'not_ready' };
    }

    const eventProperties = {
        assignment_id: assignment.id,
        survey_id: assignment.survey_id,
        is_evaluation: Boolean(assignment.course_id),
        is_anonymous: Boolean(assignment.is_anonymous),
        is_locked: Boolean(assignment.is_locked_now || assignment.is_locked),
        time_limit: assignment.time_limit ?? null,
        course_id: assignment.course_id ?? null,
    };

    try {
        const deviceId = amplitude.getDeviceId();
        const sessionId = amplitude.getSessionId();
        console.debug('[Amplitude] Tracking "Automated Survey Started" event:', {
            ...eventProperties,
            device_id: deviceId,
            session_id: sessionId,
        });

        amplitude.track('Automated Survey Started', eventProperties);

        // Flush to ensure event is sent before navigation
        await amplitude.flush();

        console.debug('[Amplitude] Successfully tracked and flushed "Automated Survey Started" event');
        return { tracked: true };
    } catch (error) {
        console.error('[Amplitude] Failed to track survey started:', error);
        return { skipped: true, reason: 'error' };
    }
};

/**
 * Track when an automated survey ends
 * Used for Targeted Replay Capture (TRC) in Amplitude
 * @param {Object} options - Survey end details
 * @param {number} options.assignmentId - The assignment ID
 * @param {number} options.surveyId - The survey ID
 * @param {boolean} options.isEvaluation - Whether this is a course evaluation
 * @param {string} options.outcome - The outcome (completed, expired, abandoned)
 * @param {number} [options.durationSeconds] - How long the survey took
 * @returns {Promise<{tracked?: boolean, skipped?: boolean, reason?: string}>}
 */
export const trackAutomatedSurveyEnded = async ({ assignmentId, surveyId, isEvaluation, outcome, durationSeconds }) => {
    if (!isSurveyTrackingEnabled()) {
        console.debug('[Amplitude] Survey tracking disabled - skipping "Automated Survey Ended" event');
        return { skipped: true, reason: 'disabled' };
    }

    // Wait for Amplitude to initialize (with 5 second timeout)
    const initResult = await waitForAmplitudeInit(5000);

    if (initResult.timedOut) {
        console.warn('[Amplitude] Initialization timeout - skipping "Automated Survey Ended" event');
        return { skipped: true, reason: 'timeout' };
    }

    if (!initResult.initialized) {
        console.warn('[Amplitude] SDK not initialized - skipping "Automated Survey Ended" event');
        return { skipped: true, reason: 'not_initialized' };
    }

    // Double-check initialization state
    if (!isAmplitudeInitialized()) {
        console.warn('[Amplitude] SDK not ready - skipping "Automated Survey Ended" event');
        return { skipped: true, reason: 'not_ready' };
    }

    const eventProperties = {
        assignment_id: assignmentId,
        survey_id: surveyId,
        is_evaluation: Boolean(isEvaluation),
        outcome: outcome || 'completed',
        duration_seconds: durationSeconds ?? null,
    };

    try {
        const deviceId = amplitude.getDeviceId();
        const sessionId = amplitude.getSessionId();
        console.debug('[Amplitude] Tracking "Automated Survey Ended" event:', {
            ...eventProperties,
            device_id: deviceId,
            session_id: sessionId,
        });

        amplitude.track('Automated Survey Ended', eventProperties);

        // Flush to ensure event is sent before navigation
        await amplitude.flush();

        console.debug('[Amplitude] Successfully tracked and flushed "Automated Survey Ended" event');
        return { tracked: true };
    } catch (error) {
        console.error('[Amplitude] Failed to track survey ended:', error);
        return { skipped: true, reason: 'error' };
    }
};

/**
 * Enrich event properties with the standard targeting context every custom
 * event carries: vertical, role names, primary role, and current route name.
 * Caller-supplied props win over the defaults.
 * @param {Object} [props]
 * @returns {Object}
 */
export const enrichEventProperties = (props = {}) => {
    const user = store.state.Auth?.user;
    const { roleNames } = buildAmplitudeUserTraits(user);

    return {
        vertical: getAmplitudeVertical(),
        role_names: roleNames,
        primary_role: getPrimaryRoleName(roleNames),
        route_name: store.state.Route?.name ?? null,
        ...props,
    };
};

/**
 * Track a custom Amplitude event, enriched with role/vertical/route context so
 * Amplitude Guides & Surveys can target on it. Safe to call before the SDK is
 * ready — it waits for init and no-ops gracefully if unavailable.
 * @param {string} eventName - Title Case event name (e.g. "Page Viewed").
 * @param {Object} [eventProperties] - snake_case event properties.
 * @returns {Promise<{tracked?: boolean, skipped?: boolean, reason?: string}>}
 */
export const trackAmplitudeEvent = async (eventName, eventProperties = {}) => {
    const initResult = await waitForAmplitudeInit(5000);

    if (initResult.timedOut) {
        console.warn(`[Amplitude] Initialization timeout - skipping "${eventName}" event`);
        return { skipped: true, reason: 'timeout' };
    }

    if (!initResult.initialized || !isAmplitudeInitialized()) {
        console.warn(`[Amplitude] SDK not ready - skipping "${eventName}" event`);
        return { skipped: true, reason: 'not_ready' };
    }

    const enriched = enrichEventProperties(eventProperties);

    try {
        console.debug(`[Amplitude] Tracking "${eventName}" event:`, enriched);
        amplitude.track(eventName, enriched);

        return { tracked: true };
    } catch (error) {
        console.error(`[Amplitude] Failed to track "${eventName}":`, error);
        return { skipped: true, reason: 'error' };
    }
};

/**
 * Track a semantic page view (a named, conditionable trigger on top of
 * Amplitude's automatic page-view capture).
 * @param {string} page - Page name (e.g. "Admin Dashboard").
 * @param {Object} [props]
 * @returns {Promise<{tracked?: boolean, skipped?: boolean, reason?: string}>}
 */
export const trackAmplitudePageView = (page, props = {}) => trackAmplitudeEvent('Page Viewed', { page, ...props });

/**
 * Track a user interaction with a named element.
 * @param {string} element - Element name (e.g. "New Course Button").
 * @param {Object} [props]
 * @returns {Promise<{tracked?: boolean, skipped?: boolean, reason?: string}>}
 */
export const trackAmplitudeClick = (element, props = {}) =>
    trackAmplitudeEvent('Element Clicked', { element, ...props });

export default {
    install(app) {
        // Create the initialization promise before starting
        amplitudeInitPromise = new Promise(resolve => {
            amplitudeInitResolver = resolve;
        });

        // Initialize Amplitude only if the feature is enabled and we have a user
        let amplitudeInitialized = false;

        const initAmplitude = async () => {
            if (amplitudeInitialized) {
                return;
            }

            const { user } = store.state.Auth;
            const featureClass = store.state?.features?.AMPLITUDE;

            if (!user) {
                // No user yet - don't resolve promise, wait for user to load
                console.debug('[Amplitude] No user yet - waiting for user to load');
                return;
            }

            if (!user.hasActiveFeature(featureClass)) {
                // Feature disabled - resolve promise so tracking doesn't block forever
                amplitudeInitResolver?.();
                console.debug('[Amplitude] Feature disabled - initialization skipped');
                return;
            }

            const apiKey = setting('amplitude_api_key');
            const instanceId = setting('amplitude_instance_id');

            if (!apiKey || !instanceId) {
                console.warn('[Amplitude] Analytics is not configured; initialization skipped');
                amplitudeInitResolver?.();
                return;
            }

            amplitudeInitialized = true;

            try {
                const sessionReplayTracking = sessionReplayPlugin({
                    blockClass: 'is-amplitude-block',
                    maskTextClass: 'is-amplitude-mask',
                    replaysSessionSampleRate: 1.0,
                    replaysOnErrorSampleRate: 1.0,
                    maskAllInputs: true,
                    maskAllText: false,
                    forceSessionTracking: true,
                    recordCanvas: true,
                    recordCrossOriginIframes: true,
                    configServerUrl: amplitudeReplayConfigServerUrl(),
                    trackServerUrl: amplitudeReplayTrackServerUrl(),
                });

                amplitude.add(sessionReplayTracking);

                const userId = user.id.toString();

                await amplitude.init(apiKey, {
                    deviceId: userId,
                    userId,
                    serverZone: 'US',
                    defaultTracking: {
                        sessions: true,
                        pageViews: true,
                        formInteractions: true,
                        fileDownloads: true,
                    },
                    autocapture: true,
                    transport: 'xhr',
                    useBeacon: false,
                    serverUrl: amplitudeServerUrl(),
                    remoteConfig: {
                        serverUrl: amplitudeReplayConfigServerUrl(),
                    },
                    minIdLength: 1,
                    trackingOptions: {
                        ipAddress: true,
                        language: true,
                        platform: true,
                        region: true,
                        version: true,
                    },
                }).promise;

                amplitude.setDeviceId(userId);
                amplitude.setUserId(userId);

                await syncAmplitudeUserProfile(amplitude, user, instanceId);

                // Log initialization details for debugging
                const deviceId = amplitude.getDeviceId();
                const sessionId = amplitude.getSessionId();
                console.debug('[Amplitude] Initialized successfully:', {
                    device_id: deviceId,
                    session_id: sessionId,
                    user_id: userId,
                    session_replay_enabled: true,
                });

                // Resolve the initialization promise
                amplitudeInitResolver?.();

                // Expose amplitude instance and survey tracking methods
                app.config.globalProperties.$amplitude = amplitude;
                app.config.globalProperties.$syncAmplitudeUserProfile = user =>
                    syncAmplitudeUserProfile(amplitude, user, instanceId);
                app.config.globalProperties.$trackAutomatedSurveyStarted = trackAutomatedSurveyStarted;
                app.config.globalProperties.$trackAutomatedSurveyEnded = trackAutomatedSurveyEnded;
                app.config.globalProperties.$trackEvent = trackAmplitudeEvent;
                app.config.globalProperties.$trackPageView = trackAmplitudePageView;
                app.config.globalProperties.$trackClick = trackAmplitudeClick;

                try {
                    await bootAmplitudeEngagement(apiKey, user);
                } catch (error) {
                    console.error('[Amplitude] Failed to initialize engagement browser:', error);
                }
            } catch (error) {
                // Allow a later Auth.user watch to retry if init fails (e.g. network)
                amplitudeInitialized = false;
                console.error('[Amplitude] Initialization failed:', error);
            }
        };

        // Watch for user changes in the store

        store.watch(
            state => state.Auth.user,
            newUser => {
                if (newUser) {
                    initAmplitude();
                }
            },
            { immediate: true }
        );
    },
};
